import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import CourseSection from './components/CourseSection';
import Footer from './components/Footer';
import { courseSections } from '../../mocks/courses';

export default function HomePage() {
  const location = useLocation();
  const povoSection = courseSections.find((s) => s.brand === 'povos-do-brasil');

  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  // Scroll to section when navigating from another page with state
  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (scrollTo) {
      const timer = setTimeout(() => {
        const el = document.getElementById(scrollTo);
        el?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) return;
    if (contactForm.message.length > 500) return;

    setContactStatus('sending');
    try {
      const params = new URLSearchParams();
      params.append('name', contactForm.name);
      params.append('email', contactForm.email);
      params.append('message', contactForm.message);

      await fetch('https://readdy.ai/api/form/d73eieclqq77n94m7hk0', {
        method: 'POST',
        body: params,
      });

      setContactStatus('success');
      setContactForm({ name: '', email: '', message: '' });
    } catch {
      setContactStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />

      {/* ── Povos do Brasil — seção principal ── */}
      {povoSection && (
        <section id="povos-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <CourseSection
            title={povoSection.title}
            subtitle={povoSection.subtitle}
            brand={povoSection.brand}
            courses={povoSection.courses}
          />
        </section>
      )}

      {/* ── Sobre ── */}
      <section id="sobre-section" className="py-20 bg-[#FBF5ED]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section label */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-[#C85C2A]"></div>
            <div className="w-3 h-3 rounded-full bg-[#E8A42A]"></div>
            <div className="w-3 h-3 rounded-full bg-[#2D6A4F]"></div>
            <span className="text-xs font-bold text-[#C85C2A] tracking-widest uppercase ml-1">Sobre nós</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left text */}
            <div>
              <h2 className="text-4xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: '\'Playfair Display\', Georgia, serif' }}>
                Quem é a <span className="text-[#C85C2A]">Editora LT</span>
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                A Editora LT é uma empresa dedicada ao desenvolvimento de materiais pedagógicos e cursos de formação continuada para educadores brasileiros. Acreditamos que professores bem formados transformam realidades.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Nossa Série <strong className="text-[#C85C2A]">Povos do Brasil</strong> foi criada para que educadores de todo o país possam conhecer e valorizar a diversidade cultural, étnica e histórica que forma a identidade brasileira — levando esse conhecimento para dentro das salas de aula.
              </p>

              {/* Values grid */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-seedling-line', color: '#2D6A4F', label: 'Educação transformadora' },
                  { icon: 'ri-group-line', color: '#C85C2A', label: 'Valorização da diversidade' },
                  { icon: 'ri-medal-line', color: '#E8A42A', label: 'Qualidade pedagógica' },
                  { icon: 'ri-heart-line', color: '#C85C2A', label: 'Compromisso social' },
                ].map((v, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-[#C85C2A]/10">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: v.color + '18' }}>
                      <i className={`${v.icon} text-lg`} style={{ color: v.color }}></i>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{v.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image + stats */}
            <div className="flex flex-col gap-6">
              <div className="relative rounded-3xl overflow-hidden" style={{ height: '340px' }}>
                <img
                  src="https://public.readdy.ai/ai/img_res/b15e144b633569be8411c0fff6f669e9.jpg"
                  alt="Educadores Editora LT"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A0A00]/50 to-transparent"></div>
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-white text-sm font-semibold">Formando educadores comprometidos com a diversidade</p>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: '6+', label: 'Cursos ativos' },
                  { value: '100%', label: 'Online e gratuito' },
                  { value: 'MEC', label: 'Alinhado à BNCC' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 text-center border border-[#C85C2A]/10">
                    <div className="text-2xl font-black text-[#C85C2A] mb-1">{stat.value}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contato ── */}
      <section id="contato-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-[#C85C2A]"></div>
                <div className="w-3 h-3 rounded-full bg-[#E8A42A]"></div>
                <div className="w-3 h-3 rounded-full bg-[#2D6A4F]"></div>
                <span className="text-xs font-bold text-[#C85C2A] tracking-widest uppercase ml-1">Fale conosco</span>
              </div>
              <h2 className="text-4xl font-black text-gray-900 mb-5 leading-tight" style={{ fontFamily: '\'Playfair Display\', Georgia, serif' }}>
                Entre em <span className="text-[#C85C2A]">Contato</span>
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Tem dúvidas sobre os cursos, parceria institucional ou quer saber mais sobre a Série Povos do Brasil? Mande uma mensagem — nossa equipe responde em até 48 horas.
              </p>

              <div className="space-y-4">
                {[
                  { icon: 'ri-mail-line', label: 'E-mail', value: 'contato@editoralt.com.br' },
                  { icon: 'ri-instagram-line', label: 'Instagram', value: '@editoralt' },
                  { icon: 'ri-time-line', label: 'Atendimento', value: 'Seg–Sex, 8h às 18h' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#C85C2A]/10 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-xl text-[#C85C2A]`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                      <p className="text-sm font-semibold text-gray-800">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right form */}
            <div className="bg-[#FBF5ED] rounded-3xl p-8 border border-[#C85C2A]/10">
              {contactStatus === 'success' ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#2D6A4F]/15 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-[#2D6A4F]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Mensagem enviada!</h3>
                  <p className="text-gray-500 text-sm mb-6">Obrigado pelo contato. Responderemos em até 48 horas.</p>
                  <button
                    onClick={() => setContactStatus('idle')}
                    className="text-[#C85C2A] font-semibold text-sm hover:underline cursor-pointer"
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Mande sua mensagem</h3>
                  <form data-readdy-form onSubmit={handleContactSubmit} className="space-y-4">
                    {/* Nome */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nome completo <span className="text-[#C85C2A]">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                          <i className="ri-user-line text-lg"></i>
                        </div>
                        <input
                          type="text"
                          name="name"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="Seu nome"
                          required
                          className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C85C2A] transition-all"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        E-mail <span className="text-[#C85C2A]">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                          <i className="ri-mail-line text-lg"></i>
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="seu@email.com"
                          required
                          className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C85C2A] transition-all"
                        />
                      </div>
                    </div>

                    {/* Mensagem */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mensagem <span className="text-[#C85C2A]">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={contactForm.message}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setContactForm({ ...contactForm, message: e.target.value });
                          }
                        }}
                        placeholder="Como podemos ajudar você?"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C85C2A] transition-all resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right mt-1">
                        {contactForm.message.length}/500
                      </p>
                    </div>

                    {contactStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        Erro ao enviar. Tente novamente.
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={contactStatus === 'sending'}
                      className="w-full text-white font-bold py-3.5 rounded-xl transition-all hover:opacity-90 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                      style={{ background: 'linear-gradient(135deg, #C85C2A, #E8A42A)' }}
                    >
                      {contactStatus === 'sending' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          Enviar mensagem
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
